import React, { useEffect, useState } from 'react';
import { ArrowLeft, Shield, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminDashboard from './AdminDashboard';
import { useAuthStore } from '../store/authStore';

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, accessToken } = useAuthStore();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // 관리자 권한 확인
  useEffect(() => {
    const checkAdminAccess = async () => {
      // 1단계: 인증 확인
      if (!isAuthenticated || !user || !accessToken) {
        console.warn('🚫 관리자 페이지 접근 거부: 인증되지 않음');
        setIsCheckingAuth(false);
        setIsAuthorized(false);
        setTimeout(() => navigate('/admin-login'), 1000);
        return;
      }

      // 2단계: 관리자 권한 확인
      if (user.role !== 'ADMIN') {
        console.warn('🚫 관리자 페이지 접근 거부: 권한 없음', { 
          userId: user.id, 
          userRole: user.role 
        });
        setIsCheckingAuth(false);
        setIsAuthorized(false);
        setTimeout(() => {
          alert('관리자 권한이 필요합니다.');
          navigate('/');
        }, 1000);
        return;
      }

      // 3단계: 백엔드에서 권한 재확인 (이중 검증)
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_URL}/admin/stats`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            console.warn('🚫 관리자 페이지 접근 거부: 인증 토큰 무효');
            setIsCheckingAuth(false);
            setIsAuthorized(false);
            setTimeout(() => navigate('/admin-login'), 1000);
            return;
          }
          if (response.status === 403) {
            console.warn('🚫 관리자 페이지 접근 거부: 관리자 권한 없음');
            setIsCheckingAuth(false);
            setIsAuthorized(false);
            setTimeout(() => {
              alert('관리자 권한이 필요합니다.');
              navigate('/');
            }, 1000);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        // 권한 확인 성공
        console.log('✅ 관리자 페이지 접근 허용:', { userId: user.id, role: user.role });
        setIsCheckingAuth(false);
        setIsAuthorized(true);
      } catch (error) {
        console.error('❌ 관리자 권한 확인 실패:', error);
        setIsCheckingAuth(false);
        setIsAuthorized(false);
        setTimeout(() => {
          alert('관리자 권한 확인 중 오류가 발생했습니다.');
          navigate('/');
        }, 1000);
      }
    };

    checkAdminAccess();
  }, [isAuthenticated, user, accessToken, navigate]);

  // 권한 확인 중
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <Shield className="w-16 h-16 text-blue-600 animate-pulse" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">권한 확인 중...</h2>
          <p className="text-gray-600">관리자 권한을 확인하고 있습니다.</p>
        </div>
      </div>
    );
  }

  // 권한 없음
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-600 mb-4">관리자 권한이 필요한 페이지입니다.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            메인으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 권한 확인 완료 - 관리자 대시보드 표시
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            메인으로 돌아가기
          </button>
          <h1 className="text-lg font-semibold text-gray-900">관리자 대시보드</h1>
          <div className="flex items-center">
            <span className="text-sm text-gray-600">{user?.name}</span>
          </div>
        </div>
      </div>

      {/* AdminDashboard 컴포넌트 */}
      <AdminDashboard />
    </div>
  );
};

export default AdminDashboardPage;
